import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import api from "@/lib/api";
import { Boxes, Building, Newspaper, MessageSquare, Mail, TrendingUp, UserPlus, ArrowRight } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

const CARDS = [
  { key: "solutions", label: "Solutions", icon: Boxes, to: "/admin/solutions" },
  { key: "projects", label: "Projects", icon: Building, to: "/admin/projects" },
  { key: "blog_posts", label: "Blog Posts", icon: Newspaper, to: "/admin/blog" },
  { key: "leads", label: "Contact Leads", icon: MessageSquare, to: "/admin/leads" },
  { key: "leads_new", label: "New Leads", icon: TrendingUp, to: "/admin/leads" },
  { key: "newsletter", label: "Newsletter", icon: Mail, to: "/admin/newsletter" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [mmpStats, setMmpStats] = useState({});
  const [mmpRecent, setMmpRecent] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setLeads(r.data.slice(0, 5))).catch(() => {});
    axios.get(`${API}/admin-mmp/stats`).then((r) => setMmpStats(r.data)).catch(() => {});
    axios.get(`${API}/admin-mmp/profiles?page_size=5`).then((r) => setMmpRecent(r.data.items || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-display text-4xl font-black text-[color:var(--mia-navy)] tracking-tight">Dashboard</h1>
        <p className="text-[color:var(--mia-muted)] mt-2">Overview of content, engagement and enquiries.</p>
      </div>

      {/* Prominent Careers Submissions banner */}
      <Link
        to="/admin/applications"
        className="block mb-6 bg-gradient-to-br from-[color:var(--mia-navy)] to-slate-800 text-white rounded-2xl p-6 sm:p-7 hover:from-slate-800 hover:to-slate-900 transition-colors"
        data-testid="dashboard-careers-banner"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[color:var(--mia-green)]/15 text-[color:var(--mia-green)] flex items-center justify-center shrink-0">
              <UserPlus className="w-7 h-7" />
            </div>
            <div>
              <div className="text-[0.65rem] font-bold tracking-[0.24em] uppercase text-[color:var(--mia-green)]">Join Our Team · Submissions</div>
              <div className="font-display font-black text-3xl sm:text-4xl mt-1">
                {mmpStats.total ?? "—"} <span className="text-base font-semibold text-white/70">total</span>
                {mmpStats.new > 0 && <span className="ml-3 text-sm font-bold bg-[color:var(--mia-green)] text-[color:var(--mia-navy)] px-3 py-1 rounded-full align-middle">{mmpStats.new} NEW</span>}
              </div>
              <div className="text-white/70 text-sm mt-1">
                Shortlisted: {mmpStats.shortlisted ?? 0} · Contacted: {mmpStats.contacted ?? 0} · Recommended: {mmpStats.recommended ?? 0}
              </div>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 font-bold text-sm uppercase tracking-widest text-[color:var(--mia-green)]">
            Open Careers <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </Link>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {CARDS.map((c) => (
          <Link key={c.key} to={c.to} className="p-6 bg-white rounded-2xl border border-slate-100 card-lift" data-testid={`stat-${c.key}`}>
            <div className="w-10 h-10 rounded-full bg-[color:var(--mia-blue)]/10 text-[color:var(--mia-blue)] flex items-center justify-center"><c.icon className="w-5 h-5" /></div>
            <div className="font-display text-3xl font-black text-[color:var(--mia-navy)] mt-4">{stats[c.key] ?? "—"}</div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--mia-muted)] mt-1">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold text-[color:var(--mia-navy)]">Recent Career Submissions</h3>
            <Link to="/admin/applications" className="text-sm font-semibold text-[color:var(--mia-blue)]">View all →</Link>
          </div>
          {mmpRecent.length === 0 ? (
            <div className="text-[color:var(--mia-muted)] py-8 text-center">No submissions yet. New &ldquo;Join Our Team&rdquo; applicants will appear here.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {mmpRecent.map((p) => (
                <li key={p.unique_id} className="py-3.5 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-semibold text-[color:var(--mia-navy)] truncate">{p.full_name} · <span className="font-normal text-[color:var(--mia-muted)]">{p.profession}</span></div>
                    <div className="text-xs text-[color:var(--mia-muted)] truncate">{p.email} · <span className="font-mono">{p.unique_id}</span></div>
                  </div>
                  <span className={`text-[0.6rem] px-2 py-1 rounded-full uppercase tracking-widest font-bold shrink-0 ${p.status === "Shortlisted" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{p.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold text-[color:var(--mia-navy)]">Recent Enquiries</h3>
            <Link to="/admin/leads" className="text-sm font-semibold text-[color:var(--mia-blue)]">View all →</Link>
          </div>
          {leads.length === 0 ? (
            <div className="text-[color:var(--mia-muted)] py-8 text-center">No leads yet. New enquiries will appear here.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {leads.map((l) => (
                <li key={l.id} className="py-3.5 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-semibold text-[color:var(--mia-navy)] truncate">{l.full_name} · {l.email}</div>
                    <div className="text-sm text-[color:var(--mia-muted)] line-clamp-1">{l.message}</div>
                  </div>
                  <div className="text-xs text-[color:var(--mia-muted)] shrink-0">{new Date(l.created_at).toLocaleDateString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
